<script setup lang="ts">
	import axios from '@/lib/axios'
	import { computed, onMounted, reactive, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import ModalComponent from '@/components/ModalComponent.vue'

	import { useAuth } from '@/composables/useAuth'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import type { CostCenter } from '@/interfaces/costCenter'
	import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

	import { canWriteTravelerProfile } from '@/modules/hotels/helpers/travelerProfileWrites'
	import type { Department } from '@/interfaces/department'

	import type { User } from '@/modules/users/interfaces/user'

	interface Props {
		user: User
		costCenter?: number
		attemptedToSubmit: boolean
		required?: boolean
	}

	interface Context {
		mandatory: boolean
		departmentId: number | null
		departments: Department[]
		costCenters: CostCenter[]
	}

	/**
	 * Viajeros a los que ya se les asignó CECO en esta sesión.
	 *
	 * `props.user` sale de una caché de Pinia con queryKey estático que nadie
	 * refresca tras el PUT, así que una segunda reserva del mismo viajero vería
	 * `costCenterId` vacío y reabriría el select para sobrescribir lo ya guardado.
	 * Vive fuera del `setup` a propósito: lo comparten todas las instancias.
	 */
	const costCentersAssignedThisSession = new Set<number>()

	const props = withDefaults(defineProps<Props>(), { required: false })
	const emit = defineEmits<{
		(event: 'update:costCenter', value: number | undefined): void
		(event: 'mandatory-change', value: boolean): void
	}>()
	const toast = useToast()
	const { hasPermissionUser } = useHasPermissionUser()
	const COMPANY = 'App\\Models\\Company'
	const canViewDepartments = computed(() =>
		hasPermissionUser('view-departments', COMPANY)
	)
	const canCreateDepartments = computed(() =>
		hasPermissionUser('create-departments', COMPANY)
	)
	/**
	 * Asignar departamento o CECO escribe el PERFIL del viajero, que puede ser otra
	 * persona. `UpdateUserRequest::authorize()` exige `update-users` sobre su
	 * empresa (o que seas tú mismo), así que sin eso el PUT vuelve 403 — y un 403
	 * saca de la reserva por el interceptor global. Se comprueba antes de disparar,
	 * no después de que falle.
	 */
	const { user: authUser } = useAuth()
	const canWriteProfile = computed(() =>
		canWriteTravelerProfile({
			actorId: authUser.value?.id,
			travelerId: props.user.id,
			hasUpdateUsers: hasPermissionUser('update-users', COMPANY),
		})
	)
	/**
	 * Excepción pedida por producto: el departamento solo se toca aquí cuando el
	 * viajero llega SIN departamento y SIN centro de costos — el caso que antes
	 * era callejón sin salida. Quien ya tiene asignación la cambia desde su
	 * perfil, no desde una reserva.
	 *
	 * Es una foto del perfil al montar (y al cambiar de viajero), no un computed
	 * sobre el estado vivo: asignar aquí pone `department_id`, y recalcularlo
	 * cerraría el select a media reserva dejando sin salida a quien acaba de
	 * elegir mal.
	 */
	const startedUnassigned = ref(
		!props.user.departmentId && !props.user.costCenterId
	)
	const canEditDepartment = computed(
		() =>
			canViewDepartments.value &&
			canWriteProfile.value &&
			startedUnassigned.value
	)
	/**
	 * El CECO se abre con su propia condición —no tener CECO— y no con la del
	 * departamento. Si dependiera de `startedUnassigned`, el viajero CON
	 * departamento pero SIN centro de costos quedaría con el select bloqueado y
	 * sin poder crear ninguno: en una empresa con CECO obligatorio eso es no
	 * poder reservar.
	 *
	 * Foto del perfil al montar, igual que el departamento: asignarlo aquí no
	 * debe cerrar el select en media reserva.
	 */
	const startedWithoutCostCenter = ref(
		!props.user.costCenterId && !costCentersAssignedThisSession.has(props.user.id)
	)
	const canEditCostCenter = computed(() => startedWithoutCostCenter.value)
	const savingCostCenter = ref(false)
	const loading = ref(false)
	const creating = ref(false)
	const modal = ref(false)
	const departmentModal = ref(false)
	const creatingDepartment = ref(false)
	const savingDepartment = ref(false)
	const newDepartmentName = ref('')
	const departmentLinked = ref(Boolean(props.user.departmentId))
	const departments = ref<Department[]>([])
	/** Catálogo completo de la empresa; `departments` solo trae el del viajero. */
	const companyDepartments = ref<Department[]>([])
	const costCenters = ref<CostCenter[]>([])
	const serverRequired = ref<boolean | null>(null)
	const selectedDepartmentId = ref<number | null>(
		props.user.departmentId || null
	)
	const selectedCostCenter = ref<number | null>(props.costCenter || null)
	const isRequired = computed(() => serverRequired.value ?? props.required)
	const form = reactive({ key: '', name: '' })
	const departmentOptions = computed(() =>
		companyDepartments.value.length ? companyDepartments.value : departments.value
	)
	const departmentName = computed(
		() =>
			departmentOptions.value.find(
				(department) => department.id === selectedDepartmentId.value
			)?.name ||
			props.user.departmentName ||
			'Sin departamento asignado'
	)
	const costCenterName = computed(
		() =>
			costCenters.value.find((item) => item.id === selectedCostCenter.value)
				?.name ||
			props.user.costCenterName ||
			'Sin centro de costos asignado'
	)
	const errorMessage = (error: unknown, fallback: string) =>
		(error as { response?: { data?: { message?: string } } })?.response?.data
			?.message ?? fallback

	const loadContext = async (departmentId = selectedDepartmentId.value) => {
		loading.value = true
		try {
			const { data } = await axios.get<Context>(
				`/api/booking/travelers/${props.user.id}/cost-centers`,
				{ params: { department_id: departmentId || undefined } }
			)
			serverRequired.value = data.mandatory
			emit('mandatory-change', data.mandatory)
			departments.value = data.departments || []
			costCenters.value = data.costCenters || []
			departmentLinked.value = Boolean(data.departmentId)
			if (data.departmentId) selectedDepartmentId.value = data.departmentId

			if (
				selectedCostCenter.value &&
				!costCenters.value.some((item) => item.id === selectedCostCenter.value)
			) {
				selectedCostCenter.value = null
				emit('update:costCenter', undefined)
			}

			// El CECO del perfil no está en el catálogo del departamento (lo movieron
			// o lo borraron). Dejarlo bloqueado enseñaría un centro de costos que no
			// se va a mandar y, con CECO obligatorio, no habría forma de corregirlo
			// desde la reserva: se reabre el select.
			if (!canEditCostCenter.value && !selectedCostCenter.value) {
				startedWithoutCostCenter.value = true
			}
		} catch (error: any) {
			toast.error(
				error?.response?.data?.message ||
					'No fue posible cargar los centros de costos'
			)
		} finally {
			loading.value = false
		}
	}

	const loadDepartments = async () => {
		if (!canEditDepartment.value || !props.user.companyId) return
		try {
			const { data } = await axios.get<{ data: Department[] }>(
				`/api/companies/${props.user.companyId}/departments`,
				// El tope de 100 es arbitrario: una empresa con más departamentos
				// queda truncada en silencio. Si eso aparece, toca un select con
				// búsqueda server-side, no subir el número.
			// `errorHandling: false`: el interceptor global de axios manda la app a
			// /error/forbidden o /error/not-found ante 403 y 404, y perder la reserva
			// a medias por esto es peor que el fallo que se quiere reportar.
				{
					params: { per_page: 100 },
					errorHandling: false,
				} as CustomAxiosRequestConfig
			)
			companyDepartments.value = data?.data || []
		} catch {
			// Sin permiso real o sin empresa: el bloque cae a solo lectura.
			companyDepartments.value = []
		}
	}

	/**
	 * El departamento NO viaja en la reserva: vive en el perfil del viajero y el
	 * backend exige que el CECO elegido pertenezca a `traveler.department_id`
	 * (BookingCostCenterValidator). Por eso cambiarlo aquí lo persiste en el
	 * usuario — es permanente, no solo para esta reserva.
	 */
	const assignDepartment = async (departmentId: number) => {
		savingDepartment.value = true
		try {
			await axios.put(
				`/api/users/${props.user.id}`,
				{ departmentId },
			// `errorHandling: false`: el interceptor global de axios manda la app a
			// /error/forbidden o /error/not-found ante 403 y 404, y perder la reserva
			// a medias por esto es peor que el fallo que se quiere reportar.
				{ errorHandling: false } as CustomAxiosRequestConfig
			)
			selectedDepartmentId.value = departmentId
			departmentLinked.value = true
			selectedCostCenter.value = null
			emit('update:costCenter', undefined)
			await loadContext(departmentId)
			toast.success('Departamento asignado al viajero')
		} catch (error: unknown) {
			toast.error(
				errorMessage(error, 'No fue posible asignar el departamento')
			)
		} finally {
			savingDepartment.value = false
		}
	}

	const selectDepartment = async (event: Event) => {
		const value = (event.target as HTMLSelectElement).value
		const departmentId = value ? Number(value) : null
		if (!departmentId || departmentId === selectedDepartmentId.value) return
		await assignDepartment(departmentId)
	}

	const createDepartment = async () => {
		const name = newDepartmentName.value.trim()
		if (!name) {
			toast.error('Escribe el nombre del departamento')
			return
		}
		// Sin empresa la URL sale `/api/companies/null/departments` → 404. Pasa con
		// usuarios internos, que no tienen empresa; la interfaz declara `companyId`
		// obligatorio, así que el typecheck no lo caza.
		if (!props.user.companyId) {
			toast.error('El viajero no tiene empresa: no se puede crear departamento')
			return
		}
		creatingDepartment.value = true
		try {
			const { data } = await axios.post<{ data?: Department; id?: number }>(
				`/api/companies/${props.user.companyId}/departments`,
				{ name },
			// `errorHandling: false`: el interceptor global de axios manda la app a
			// /error/forbidden o /error/not-found ante 403 y 404, y perder la reserva
			// a medias por esto es peor que el fallo que se quiere reportar.
				{ errorHandling: false } as CustomAxiosRequestConfig
			)
			await loadDepartments()
			// El POST responde el resource envuelto; si no trae id directo se
			// busca por nombre en la lista recién recargada.
			const createdId =
				data?.data?.id ??
				data?.id ??
				companyDepartments.value.find((item) => item.name === name)?.id
			departmentModal.value = false
			newDepartmentName.value = ''
			if (!createdId) {
				toast.error('Departamento creado, pero no se pudo seleccionar')
				return
			}
			await assignDepartment(Number(createdId))
		} catch (error: unknown) {
			toast.error(errorMessage(error, 'No fue posible crear el departamento'))
		} finally {
			creatingDepartment.value = false
		}
	}

	/**
	 * El CECO viaja en la reserva Y en el perfil: guardarlo en el usuario es lo
	 * que lo deja visible y editable después en el apartado de usuarios. Solo se
	 * escribe en la primera asignación; si ya traía uno, este componente no lo
	 * toca.
	 */
	const assignCostCenterToProfile = async (costCenterId: number) => {
		// Sin permiso el CECO se queda solo en la reserva: el backend rechazaría el
		// PUT con 403 y eso, además de no guardar nada, sacaría de la reserva.
		if (!canEditCostCenter.value || !canWriteProfile.value) return
		savingCostCenter.value = true
		try {
			await axios.put(
				`/api/users/${props.user.id}`,
				{ costCenterId },
			// `errorHandling: false`: el interceptor global de axios manda la app a
			// /error/forbidden o /error/not-found ante 403 y 404, y perder la reserva
			// a medias por esto es peor que el fallo que se quiere reportar.
				{ errorHandling: false } as CustomAxiosRequestConfig
			)
			costCentersAssignedThisSession.add(props.user.id)
		} catch (error: unknown) {
			toast.error(
				errorMessage(
					error,
					'El centro de costos quedó en la reserva, pero no se pudo guardar en el perfil'
				)
			)
		} finally {
			savingCostCenter.value = false
		}
	}

	const selectCostCenter = async (event: Event) => {
		const value = (event.target as HTMLSelectElement).value
		selectedCostCenter.value = value ? Number(value) : null
		emit('update:costCenter', selectedCostCenter.value || undefined)
		if (selectedCostCenter.value)
			await assignCostCenterToProfile(selectedCostCenter.value)
	}

	const createCostCenter = async () => {
		if (!selectedDepartmentId.value || !form.key.trim() || !form.name.trim()) {
			toast.error('Completa la llave y el nombre del centro de costos')
			return
		}
		creating.value = true
		try {
			const key = form.key.trim()
			const name = form.name.trim()
			const { data } = await axios.post(
				`/api/booking/travelers/${props.user.id}/cost-centers`,
				{
					key,
					name,
					departmentId: selectedDepartmentId.value,
				}
			)
			await loadContext()
			const createdId =
				data?.id ||
				data?.data?.id ||
				costCenters.value.find((item) => item.key === key && item.name === name)
					?.id
			if (createdId) {
				selectedCostCenter.value = Number(createdId)
				emit('update:costCenter', Number(createdId))
				// Departamento recién creado o sin CECOs: el que se acaba de crear
				// es el que queda en el perfil del viajero.
				await assignCostCenterToProfile(Number(createdId))
			}
			modal.value = false
			form.key = ''
			form.name = ''
			toast.success('Centro de costos creado y seleccionado')
		} catch (error: any) {
			const firstError = Object.values(
				error?.response?.data?.errors || {}
			).flat()[0]
			toast.error(
				String(
					firstError ||
						error?.response?.data?.message ||
						'No fue posible crear el centro de costos'
				)
			)
		} finally {
			creating.value = false
		}
	}

	watch(
		() => props.costCenter,
		(value) => {
			selectedCostCenter.value = value || null
		}
	)
	watch(
		() => props.user.id,
		() => {
			selectedDepartmentId.value = props.user.departmentId || null
			departmentLinked.value = Boolean(props.user.departmentId)
			selectedCostCenter.value = props.costCenter || null
			serverRequired.value = null
			startedUnassigned.value =
				!props.user.departmentId && !props.user.costCenterId
			startedWithoutCostCenter.value = !props.user.costCenterId
			companyDepartments.value = []
			emitProfileCostCenter()
			loadContext()
			loadDepartments()
		}
	)
	/**
	 * Viajero ya asignado: su CECO de perfil es el de la reserva. Se emite al
	 * montar porque el select queda bloqueado y nadie va a dispararlo a mano.
	 */
	const emitProfileCostCenter = () => {
		if (canEditCostCenter.value || props.costCenter) return
		if (!props.user.costCenterId) return
		selectedCostCenter.value = props.user.costCenterId
		emit('update:costCenter', props.user.costCenterId)
	}

	onMounted(() => {
		emitProfileCostCenter()
		loadContext()
		loadDepartments()
	})
</script>

<template>
	<div class="space-y-3">
		<div
			v-if="loading"
			class="text-sm text-gray-500"
		>
			Cargando centros de costos…
		</div>

		<div
			v-else-if="!departmentLinked && !canEditDepartment"
			role="alert"
			class="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900"
		>
			<span
				class="material-symbols-outlined mt-0.5 text-xl text-amber-600"
				aria-hidden="true"
			>
				warning
			</span>
			<div>
				<p class="text-sm font-semibold">Sin departamento asignado</p>
				<p class="mt-1 text-sm text-amber-800">
					Este dato pertenece al perfil del viajero y no puede modificarse
					durante la reserva. Solicita a un administrador que asigne el
					departamento para poder elegir un centro de costos.
				</p>
			</div>
		</div>

		<template v-else>
			<div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<div class="space-y-2">
					<label
						:for="`department-${user.id}`"
						class="block text-sm font-medium text-gray-700"
					>
						Departamento
					</label>

					<div
						v-if="canEditDepartment"
						class="flex items-center gap-2"
					>
						<select
							:id="`department-${user.id}`"
							:value="selectedDepartmentId"
							:disabled="savingDepartment"
							@change="selectDepartment"
							class="w-full min-h-12 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-500 disabled:opacity-60"
						>
							<option
								value=""
								disabled
							>
								Selecciona un departamento
							</option>
							<option
								v-for="item in departmentOptions"
								:key="item.id"
								:value="item.id"
							>
								{{ item.name }}
							</option>
						</select>
						<button
							v-if="canCreateDepartments"
							type="button"
							:disabled="savingDepartment"
							aria-label="Crear departamento"
							title="Crear departamento"
							@click="departmentModal = true"
							class="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:border-green-400 hover:bg-green-50 hover:text-green-700 disabled:opacity-60"
						>
							<span
								class="material-symbols-outlined"
								aria-hidden="true"
							>
								add
							</span>
						</button>
					</div>

					<div
						v-else
						role="textbox"
						aria-readonly="true"
						:aria-label="`Departamento asignado: ${departmentName}`"
						class="flex min-h-12 items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-800"
					>
						<span class="flex min-w-0 items-center gap-2">
							<span
								class="material-symbols-outlined text-xl text-gray-500"
								aria-hidden="true"
							>
								apartment
							</span>
							<span class="truncate font-medium">{{ departmentName }}</span>
						</span>
						<span
							class="material-symbols-outlined text-base text-gray-400"
							aria-hidden="true"
						>
							lock
						</span>
					</div>

					<p class="text-xs text-gray-500">
						{{
							canEditDepartment ?
								'Primera asignación: queda guardada en el perfil del viajero, no solo en esta reserva.'
							:	'Ya asignado en el perfil del viajero. Para cambiarlo, edítalo desde su perfil.'
						}}
					</p>
				</div>

				<div class="space-y-2">
					<label
						:for="`cost-center-${user.id}`"
						class="block text-sm font-medium text-gray-700"
					>
						Centro de costos
					</label>
					<select
						v-if="canEditCostCenter"
						:id="`cost-center-${user.id}`"
						:value="selectedCostCenter"
						:disabled="savingCostCenter"
						:data-cost-center-missing="
							isRequired && !selectedCostCenter ? 'true' : undefined
						"
						@change="selectCostCenter"
						:class="[
							'w-full min-h-12 rounded-lg border bg-white px-4 py-3 text-gray-900 transition-colors focus:ring-2',
							isRequired && attemptedToSubmit && !selectedCostCenter ?
								'border-red-400 focus:border-red-500 focus:ring-red-200'
							:	'border-gray-300 focus:border-green-500 focus:ring-green-500',
						]"
					>
						<option
							value=""
							:disabled="isRequired"
						>
							{{
								isRequired ?
									'Selecciona un centro de costos'
								:	'Sin centro de costos'
							}}
						</option>
						<option
							v-for="item in costCenters"
							:key="item.id"
							:value="item.id"
						>
							{{ item.name }} - {{ item.key }}
						</option>
					</select>

					<div
						v-else
						role="textbox"
						aria-readonly="true"
						:aria-label="`Centro de costos asignado: ${costCenterName}`"
						class="flex min-h-12 items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-800"
					>
						<span class="flex min-w-0 items-center gap-2">
							<span
								class="material-symbols-outlined text-xl text-gray-500"
								aria-hidden="true"
							>
								account_balance_wallet
							</span>
							<span class="truncate font-medium">{{ costCenterName }}</span>
						</span>
						<span
							class="material-symbols-outlined text-base text-gray-400"
							aria-hidden="true"
						>
							lock
						</span>
					</div>

					<p class="text-xs text-gray-500">
						{{
							canEditCostCenter ?
								'Primera asignación: queda guardado en el perfil del viajero.'
							:	'Ya asignado en el perfil del viajero. Para cambiarlo, edítalo desde su perfil.'
						}}
					</p>
				</div>
			</div>
			<button
				v-if="canEditCostCenter && selectedDepartmentId"
				type="button"
				@click="modal = true"
				class="w-full rounded-lg border-2 border-dashed border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:border-green-400 hover:bg-green-50"
			>
				{{
					costCenters.length ?
						'Agregar nuevo centro de costos'
					:	'Crear centro de costos para este departamento'
				}}
			</button>
		</template>

		<div
			v-if="isRequired && attemptedToSubmit && !selectedCostCenter"
			role="alert"
			class="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
		>
			<span
				class="material-symbols-outlined mt-0.5 text-base"
				aria-hidden="true"
			>
				error
			</span>
			<span>
				Selecciona un centro de costos válido para este viajero antes de
				reservar.
			</span>
		</div>

		<ModalComponent
			v-model="departmentModal"
			@close="departmentModal = false"
		>
			<template #modalBody>
				<form
					class="mx-auto max-w-md space-y-5 rounded-xl bg-white p-6"
					@submit.prevent="createDepartment"
				>
					<div>
						<h2 class="text-xl font-bold text-gray-900">Crear departamento</h2>
						<p class="mt-1 text-sm text-gray-600">
							Se crea para la empresa del viajero y queda asignado a su perfil.
						</p>
					</div>
					<input
						v-model.trim="newDepartmentName"
						required
						class="w-full rounded-lg border border-gray-300 px-4 py-3"
						placeholder="Nombre del departamento"
					/>
					<div class="flex justify-end gap-3">
						<button
							type="button"
							class="rounded-lg border px-4 py-2"
							@click="departmentModal = false"
						>
							Cancelar
						</button>
						<button
							type="submit"
							:disabled="creatingDepartment || savingDepartment"
							class="rounded-lg bg-green-600 px-4 py-2 font-medium text-white disabled:opacity-50"
						>
							{{ creatingDepartment ? 'Creando…' : 'Crear y asignar' }}
						</button>
					</div>
				</form>
			</template>
		</ModalComponent>

		<ModalComponent
			v-model="modal"
			@close="modal = false"
		>
			<template #modalBody>
				<form
					class="mx-auto max-w-md space-y-5 rounded-xl bg-white p-6"
					@submit.prevent="createCostCenter"
				>
					<div>
						<h2 class="text-xl font-bold text-gray-900">
							Crear centro de costos
						</h2>
						<p class="mt-1 text-sm text-gray-600">
							Se ligará al departamento del viajero y quedará elegido para esta
							reserva.
						</p>
					</div>
					<input
						v-model.trim="form.key"
						required
						class="w-full rounded-lg border border-gray-300 px-4 py-3"
						placeholder="Llave del centro de costos"
					/>
					<input
						v-model.trim="form.name"
						required
						class="w-full rounded-lg border border-gray-300 px-4 py-3"
						placeholder="Nombre del centro de costos"
					/>
					<div class="flex justify-end gap-3">
						<button
							type="button"
							class="rounded-lg border px-4 py-2"
							@click="modal = false"
						>
							Cancelar
						</button>
						<button
							type="submit"
							:disabled="creating"
							class="rounded-lg bg-green-600 px-4 py-2 font-medium text-white disabled:opacity-50"
						>
							{{ creating ? 'Creando…' : 'Crear y seleccionar' }}
						</button>
					</div>
				</form>
			</template>
		</ModalComponent>
	</div>
</template>
